import { useEffect, useRef } from "react";

export function Entropy({ className = "", size = 460 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const particleColor = "#E8B86A"; // gold to match brand

    class Particle {
      constructor(x, y, order) {
        this.x = x;
        this.y = y;
        this.originalX = x;
        this.originalY = y;
        this.size = 2;
        this.order = order;
        this.velocity = {
          x: (Math.random() - 0.5) * 2,
          y: (Math.random() - 0.5) * 2,
        };
        this.influence = 0;
        this.neighbors = [];
      }

      update() {
        if (this.order) {
          const dx = this.originalX - this.x;
          const dy = this.originalY - this.y;
          const chaosInfluence = { x: 0, y: 0 };
          this.neighbors.forEach((neighbor) => {
            if (!neighbor.order) {
              const distance = Math.hypot(this.x - neighbor.x, this.y - neighbor.y);
              const strength = Math.max(0, 1 - distance / 100);
              chaosInfluence.x += neighbor.velocity.x * strength;
              chaosInfluence.y += neighbor.velocity.y * strength;
              this.influence = Math.max(this.influence, strength);
            }
          });
          this.x += dx * 0.05 * (1 - this.influence) + chaosInfluence.x * this.influence;
          this.y += dy * 0.05 * (1 - this.influence) + chaosInfluence.y * this.influence;
          this.influence *= 0.99;
        } else {
          this.velocity.x += (Math.random() - 0.5) * 0.5;
          this.velocity.y += (Math.random() - 0.5) * 0.5;
          this.velocity.x *= 0.95;
          this.velocity.y *= 0.95;
          this.x += this.velocity.x;
          this.y += this.velocity.y;
          if (this.x < size / 2 || this.x > size) this.velocity.x *= -1;
          if (this.y < 0 || this.y > size) this.velocity.y *= -1;
          this.x = Math.max(size / 2, Math.min(size, this.x));
          this.y = Math.max(0, Math.min(size, this.y));
        }
      }

      draw(c) {
        const alpha = this.order ? 0.8 - this.influence * 0.5 : 0.85;
        c.fillStyle = `${particleColor}${Math.round(alpha * 255).toString(16).padStart(2, "0")}`;
        c.beginPath();
        c.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        c.fill();
      }
    }

    const particles = [];
    const gridSize = 18;
    const spacing = size / gridSize;
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const x = spacing * i + spacing / 2;
        const y = spacing * j + spacing / 2;
        const order = x < size / 2;
        particles.push(new Particle(x, y, order));
      }
    }

    function updateNeighbors() {
      particles.forEach((p) => {
        p.neighbors = particles.filter((o) => {
          if (o === p) return false;
          const d = Math.hypot(p.x - o.x, p.y - o.y);
          return d < 100;
        });
      });
    }

    let time = 0;
    let animationId;
    let lastFrame = 0;
    let isVisible = true;
    let isPageHidden = false;
    const targetInterval = 1000 / 45; // cap at 45fps to reduce GPU load

    // Pause when canvas off-screen
    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          isVisible = e.isIntersecting;
        });
      },
      { threshold: 0.01 }
    );
    visibilityObserver.observe(canvas);

    // Pause when tab hidden
    const onTabVisibility = () => {
      isPageHidden = document.hidden;
    };
    document.addEventListener("visibilitychange", onTabVisibility);

    function animate(now) {
      animationId = requestAnimationFrame(animate);
      if (isPageHidden || !isVisible) return;
      if (now - lastFrame < targetInterval) return;
      lastFrame = now;

      ctx.clearRect(0, 0, size, size);
      if (time % 45 === 0) updateNeighbors();

      particles.forEach((p) => {
        p.update();
        p.draw(ctx);
      });

      // Single batched pass for connection lines (cheaper than per-particle stroke calls)
      ctx.lineWidth = 0.6;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        for (let j = 0; j < p.neighbors.length; j++) {
          const n = p.neighbors[j];
          if (n === p || particles.indexOf(n) <= i) continue; // dedupe
          const dx = p.x - n.x;
          const dy = p.y - n.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 50) {
            const a = 0.22 * (1 - d / 50);
            ctx.strokeStyle = `${particleColor}${Math.round(a * 255).toString(16).padStart(2, "0")}`;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(n.x, n.y);
            ctx.stroke();
          }
        }
      }

      ctx.strokeStyle = `${particleColor}55`;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(size / 2, 0);
      ctx.lineTo(size / 2, size);
      ctx.stroke();

      time++;
    }

    animationId = requestAnimationFrame(animate);
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      visibilityObserver.disconnect();
      document.removeEventListener("visibilitychange", onTabVisibility);
    };
  }, [size]);

  return (
    <div
      className={`relative ${className}`}
      style={{ width: size, height: size }}
    >
      <canvas
        ref={canvasRef}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      />
    </div>
  );
}

export default Entropy;
